<template>
    <div class="p-grid">
        <div class="p-col-12">
            <div class="card p-shadow-1">
                <h5>Limpeza de Ponto</h5>
                <hr>

                <div class="p-fluid p-formgrid p-grid">
                    <div class="p-field p-col-12 p-md-5">
                        <label>Buscar CNPJ</label>
                        <div class="p-inputgroup">
                            <InputText v-model="valorBusca" placeholder="Razão social ou CNPJ" @keyup.enter="buscarCnpjs" />
                            <Button icon="pi pi-search" title="Buscar CNPJ" @click="buscarCnpjs" />
                        </div>
                    </div>
                </div>

                <DataTable :value="empresas" :loading="carregandoCnpjs" selectionMode="single" :selection.sync="empresaSelecionada"
                    dataKey="id" class="p-datatable-sm p-datatable-striped" @row-select="selecionarEmpresa">
                    <Column field="id" header="ID"></Column>
                    <Column field="razaoSocial" header="Razão social"></Column>
                    <Column field="cnpj" header="CNPJ">
                        <template #body="slotProps">
                            {{ (slotProps.data.cnpj || slotProps.data.numero) | maskCNPJ }}
                        </template>
                    </Column>
                </DataTable>
                <Paginator :rows="totalPorPagina" :totalRecords="totalEmpresas" :rowsPerPageOptions="[15, 25, 30]"
                    template="FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                    currentPageReportTemplate="Listando {first} a {last} de {totalRecords}" @page="paginarCnpjs" />

                <Message v-if="erroCarregamento" severity="error" :closable="false" class="p-mt-3">{{ erroCarregamento }}</Message>

                <template v-if="empresaSelecionada">
                    <Toolbar class="p-mt-4 p-mb-3">
                        <template slot="left">
                            <strong>{{ nomeEmpresaSelecionada }}</strong>
                        </template>
                        <template slot="right">
                            <Button label="Limpar seleção" icon="pi pi-times" class="p-button-secondary" @click="limparSelecao" />
                        </template>
                    </Toolbar>                    

                    <!-- Seleção de Funcionários com PickList -->
                    <Panel header="Funcionários" class="p-mb-4">
                        <PickList v-model="funcionariosList" :responsive="true" breakpoint="1400px"
                            :showSourceControls="false" :showTargetControls="false"
                            :dragdrop="true" sourceStyle="height: 300px" targetStyle="height: 300px">
                            <template #sourceHeader>
                                <span>Funcionários Disponíveis</span>
                            </template>
                            <template #targetHeader>
                                <span>Funcionários p/ Limpeza</span>
                            </template>
                            <template #item="{ item }">
                                {{ item && item.nome ? item.nome : '' }}
                            </template>
                        </PickList>
                        <Message v-if="carregandoFuncionarios" severity="info" :closable="false" class="p-mt-3">
                            Carregando funcionários...
                        </Message>
                    </Panel>

                    <!-- Seleção de Período -->
                    <Panel header="Período para Limpeza de Ponto" class="p-mb-4">
                        <div class="p-fluid p-formgrid p-grid">
                            <div class="p-field p-col-12 p-md-4">
                                <label>Data Inicial</label>
                                <Calendar v-model="dataInicial" dateFormat="dd/mm/yy" 
                                    :disabledDates="datasFuturas" showIcon />
                            </div>
                            <div class="p-field p-col-12 p-md-4">
                                <label>Data Final</label>
                                <Calendar v-model="dataFinal" dateFormat="dd/mm/yy" 
                                    :disabledDates="datasFuturas" showIcon />
                            </div>
                            <div class="p-field p-col-12 p-md-4">
                                <label>Dias selecionados</label>
                                <InputText :value="diasSelecionados" disabled />
                            </div>
                        </div>
                        <Message v-if="erroValidacaoPeriodo" severity="warn" :closable="false">
                            {{ erroValidacaoPeriodo }}
                        </Message>
                    </Panel>

                    <!-- Botão de Exclusão -->
                    <div class="p-mb-3">
                        <Button label="Excluir dias de ponto" icon="pi pi-trash" class="p-button-danger" 
                            @click="abrirConfirmacaoExclusao"
                            :disabled="!funcionariosList[1].length || !dataInicial || !dataFinal || erroValidacaoPeriodo || operacaoEmAndamento" 
                            :loading="operacaoEmAndamento" />
                    </div>
                </template>

                <!-- Modal de Confirmação -->
                <Dialog :visible.sync="exibirConfirmacao" header="Confirmar Exclusão" :modal="true" :style="{width: '600px'}">
                    <div class="confirmation-content">
                        <i class="pi pi-exclamation-triangle p-mr-3" style="font-size: 2rem; color:red" />
                        <div>
                            <p><strong>Atenção!</strong></p>
                            <p>Você está prestes a excluir os registros de ponto dos funcionários selecionados para o período de <strong>{{ dataInicialFormatada }} a {{ dataFinalFormatada }}</strong>.</p>
                            <p><strong>CNPJ:</strong> {{ nomeEmpresaSelecionada }}</p>
                            <p><strong>Funcionários afetados:</strong> {{ funcionariosList[1].length }}</p>
                            <p><strong>Período:</strong> {{ diasSelecionados }} dias</p>
                            <p style="color: #ff6b6b; font-weight: bold;">Esta operação irá remover os registros de ponto existentes nesse período.</p>
                            <p><strong>Deseja realmente continuar?</strong></p>
                        </div>
                    </div>
                    <template #footer>
                        <Button label="Cancelar" icon="pi pi-times" class="p-button-secondary" @click="exibirConfirmacao = false" :disabled="operacaoEmAndamento" />
                        <Button label="Confirmar exclusão" icon="pi pi-check" class="p-button-danger" @click="confirmarExclusao" :loading="operacaoEmAndamento" />
                    </template>
                </Dialog>
            </div>
        </div>
    </div>
</template>

<script>
import EmpresaService from '@/service/EmpresaService';
import LimpezaPontoService from '@/service/LimpezaPontoService';
import { exibirMensagem, exibirMensagemSis } from '@/global';

export default {
    data() {
        return {
            empresaService: null,
            limpezaPontoService: null,
            empresas: [],
            empresaSelecionada: null,
            funcionariosList: [[], []],  // [disponíveis, selecionados]
            valorBusca: '',
            pagina: 0,
            totalPorPagina: 15,
            totalEmpresas: 0,
            carregandoCnpjs: false,
            carregandoFuncionarios: false,
            erroCarregamento: '',
            erroValidacaoPeriodo: '',
            dataInicial: null,
            dataFinal: null,
            exibirConfirmacao: false,
            operacaoEmAndamento: false
        };
    },
    computed: {
        nomeEmpresaSelecionada() {
            return this.valor(this.empresaSelecionada, ['razaoSocial', 'nome', 'cnpj', 'numero']);
        },
        diasSelecionados() {
            if(!this.dataInicial || !this.dataFinal) return '0 dias';
            const diffTime = Math.abs(this.dataFinal - this.dataInicial);
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
            return diffDays + ' dias';
        },
        dataInicialFormatada() {
            return this.dataInicial ? this.dataInicial.toLocaleDateString('pt-BR') : '';
        },
        dataFinalFormatada() {
            return this.dataFinal ? this.dataFinal.toLocaleDateString('pt-BR') : '';
        },
        datasFuturas() {
            return [];
        }
    },
    watch: {
        dataInicial() {
            this.validarPeriodo();
        },
        dataFinal() {
            this.validarPeriodo();
        }
    },
    methods: {
        listarCnpjs() {
            this.carregandoCnpjs = true;
            this.empresaService.listarCnpj(this.valorBusca.replace(/[.\-/]/g, ''), this.pagina, this.totalPorPagina)
                .then(resp => {
                    this.totalEmpresas = resp.data.totalItens || 0;
                    this.empresas = resp.data.empresas || [];
                })
                .catch(erro => exibirMensagem(this, erro))
                .finally(() => { this.carregandoCnpjs = false; });
        },
        buscarCnpjs() {
            this.pagina = 0;
            this.totalEmpresas = 0;
            this.empresaSelecionada = null;
            this.funcionariosList = [[], []];
            this.listarCnpjs();
        },
        paginarCnpjs(event) {
            this.pagina = event.page;
            this.totalPorPagina = event.rows;
            this.listarCnpjs();
        },
        selecionarEmpresa(event) {
            this.empresaSelecionada = event.data;
            this.carregarFuncionarios();
        },
        carregarFuncionarios() {
            const cnpjId = this.empresaSelecionada && this.empresaSelecionada.id;
            if(!cnpjId) return;
            this.erroCarregamento = '';
            this.funcionariosList = [[], []];
            this.carregandoFuncionarios = true;
            this.limpezaPontoService.listarFuncionarios(cnpjId)
                .then(resp => {
                    const funcionarios = this.listaDaResposta(resp);
                    const funcionariosNormalizados = Array.isArray(funcionarios)
                        ? funcionarios.map(f => ({
                            id: this.valor(f, ['id', 'codigo', 'funcionarioId']),
                            nome: this.valor(f, ['nome', 'nomeCompleto', 'name', 'fullName']),
                            ...f
                        }))
                        : [];

                    if(funcionariosNormalizados.length > 0) {
                        this.funcionariosList = [funcionariosNormalizados, []];
                    } else {
                        this.erroCarregamento = 'Nenhum funcionário encontrado para este CNPJ.';
                    }
                }).catch(erro => {
                    console.error('Erro ao carregar funcionários:', erro);
                    this.erroCarregamento = 'Não foi possível carregar os funcionários. Verifique a API.';
                    exibirMensagemSis(this, erro.message || 'Erro ao carregar funcionários.', true);
                }).finally(() => {
                    this.carregandoFuncionarios = false;
                });
        },
        limparSelecao() {
            this.empresaSelecionada = null;
            this.funcionariosList = [[], []];
            this.dataInicial = null;
            this.dataFinal = null;
            this.erroValidacaoPeriodo = '';
            this.erroCarregamento = '';
        },
        validarPeriodo() {
            this.erroValidacaoPeriodo = '';
            if(!this.dataInicial || !this.dataFinal) return;
            
            if(this.dataInicial > this.dataFinal) {
                this.erroValidacaoPeriodo = 'A data inicial não pode ser posterior à data final.';
                return;
            }

            const diffTime = Math.abs(this.dataFinal - this.dataInicial);
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
            
            if(diffDays > 30) {
                this.erroValidacaoPeriodo = 'O período selecionado não pode ultrapassar 30 dias. Período selecionado: ' + diffDays + ' dias.';
            }
        },
        abrirConfirmacaoExclusao() {
            if(this.operacaoEmAndamento) return;
            if(!this.funcionariosList[1].length) {
                exibirMensagemSis(this, 'Selecione pelo menos um funcionário.', true);
                return;
            }
            if(!this.dataInicial || !this.dataFinal) {
                exibirMensagemSis(this, 'Preencha o período de datas.', true);
                return;
            }
            if(this.erroValidacaoPeriodo) {
                exibirMensagemSis(this, this.erroValidacaoPeriodo, true);
                return;
            }
            this.exibirConfirmacao = true;
        },
        confirmarExclusao() {
            if(this.operacaoEmAndamento) return;
            
            // Validar antes de enviar
            if(!this.funcionariosList[1].length) {
                exibirMensagemSis(this, 'Nenhum funcionário selecionado.', true);
                return;
            }
            if(!this.dataInicial || !this.dataFinal) {
                exibirMensagemSis(this, 'Período de datas incompleto.', true);
                return;
            }
            
            this.operacaoEmAndamento = true;
            const cnpjId = this.empresaSelecionada.id;
            const funcionarioIds = this.funcionariosList[1].map(f => f.id);
            
            this.limpezaPontoService.excluirPontoPeriodo(
                cnpjId, 
                funcionarioIds, 
                this.dataInicial, 
                this.dataFinal
            )
            .then(resp => {
                exibirMensagemSis(this, this.mensagemSucesso(resp, 'Pontos deletados com sucesso.'), false);
                this.exibirConfirmacao = false;
                this.limparSelecao();
            })
            .catch(erro => {
                exibirMensagemSis(this, erro.message || 'Erro ao excluir registros de ponto.', true);
            })
            .finally(() => {
                this.operacaoEmAndamento = false;
            });
        },
        listaDaResposta(resp) {
            const dados = resp && resp.data;
            if(Array.isArray(dados)) return dados;
            if(dados && Array.isArray(dados.entity)) return dados.entity;
            if(dados && Array.isArray(dados.items)) return dados.items;
            if(dados && Array.isArray(dados.content)) return dados.content;
            return [];
        },
        valor(objeto, campos) {
            if(!objeto) return '';
            for(let i = 0; i < campos.length; i++) {
                if(objeto[campos[i]] !== undefined && objeto[campos[i]] !== null) return objeto[campos[i]];
            }
            return '';
        },
        mensagemSucesso(resp, padrao) {
            return (resp && resp.data && (resp.data.mensagem || resp.data.message)) || padrao;
        }
    },
    created() {
        this.empresaService = new EmpresaService();
        this.limpezaPontoService = new LimpezaPontoService();
        this.listarCnpjs();
    }
};
</script>

<style scoped>
.confirmation-content {
    display: flex;
    gap: 15px;
    align-items: flex-start;
}

.confirmation-content i {
    flex-shrink: 0;
}

.confirmation-content div {
    flex: 1;
}

.confirmation-content p {
    margin: 8px 0;
    line-height: 1.5;
}
</style>
